import { isSpotify, logger, type Embed, type Spotify } from '@tunasong/models'
import invariant from 'tiny-invariant'
import { parsePx, type EmbedSizes, type ServiceApi } from './types.js'

/** Example URLs
 *
 * https://open.spotify.com/track/6fVW0THmvWdjxsoleCMPX5?si=fonyVROsRzmxRmAbqiR9FA
 * https://open.spotify.com/album/3KFWViJ1wIHAdOVLFTVzjD?si=ghMNDThvQrqUxoHbjYYjUA
 * https://open.spotify.com/playlist/4ZXCnIkTNaGmKlw2NHLMje?si=qD5UJ1NVTJ-P5m1rNdxxhg
 * https://open.spotify.com/intl-de/track/4uLU6hMCjMI75M1A2tKUQC?si=x
 *
 * spotify:album:1DFixLWuPkv3KT3TnV35m3
 *
 * Documentation:
 * https://developer.spotify.com/documentation/widgets/generate/play-button/
 */

interface EmbedController {
  addListener: (event: string, callback: (e: { data: unknown }) => void) => void
  destroy: () => void
  seek: (time: number) => void
  resume: () => void
  pause: () => void
  play: () => void
  setIframeDimensions: (width: number, height: number) => void
  iframeElement: HTMLIFrameElement | null
}
interface PlaybackState {
  position: number // in milliseconds
  duration: number // in milliseconds
  playingURI?: string
  isPaused?: boolean
  isBuffering?: boolean
}

interface IFrameAPI {
  createController: (
    el: HTMLElement,
    options: { uri: string; height?: number; width?: number },
    callback: (c: EmbedController) => void
  ) => EmbedController
}
declare global {
  interface Window {
    onSpotifyIframeApiReady: (api: IFrameAPI) => void
    spotifyIFrameAPI: IFrameAPI | undefined
  }
}

const urnMatch = /^spotify:(\w+):(\w+)$/
/** Share links outside the US carry a locale segment: `open.spotify.com/intl-de/track/<id>` (#1972) */
const urlMatch = /^https:\/\/open\.spotify\.com\/(?:intl-[a-z]{2}(?:-[a-z]{2})?\/)?(\w+)\/(\w+)(\?.*)?$/i
const iFrameMatch = /^\<iframe src=\"https:\/\/open.spotify.com\/embed\/(\w+)\/(\w+)\"/

const sizes: EmbedSizes = {
  small: {
    width: '50%',
    height: '80px',
  },
  medium: {
    width: '100%',
    height: '80px',
  },
  large: {
    width: '100%',
    height: '380px',
  },
}

export const parseSpotifyLink = (urlOrUrn?: string): Embed | null => {
  if (!urlOrUrn) {
    return null
  }
  const trimmed = urlOrUrn.trim()
  const match = trimmed.match(urnMatch) ?? trimmed.match(urlMatch) ?? trimmed.match(iFrameMatch)
  const mediaType = match?.[1] as string | undefined
  const mediaId = match?.[2]
  if (!(mediaId && mediaType)) {
    return null
  }
  const node = {
    type: 'embed',
    service: 'spotify',
    url: `https://open.spotify.com/embed/${mediaType}/${mediaId}`,
    uri: `spotify:${mediaType}:${mediaId}`,
    mediaId,
    size: 'medium',
    children: [{ text: '' }],
  } satisfies Spotify
  return node
}

/** @see https://developer.spotify.com/documentation/embeds */
const getApi = (embed: Embed, el: HTMLIFrameElement, container: HTMLDivElement) => {
  invariant(isSpotify(embed))
  const scriptId = 'spotify-iframe-api'
  // Check if the youtube-iframe-api script is already present
  if (!document.getElementById(scriptId)) {
    const tag = document.createElement('script')
    tag.id = scriptId
    tag.src = 'https://open.spotify.com/embed/iframe-api/v1'
    const firstScriptTag = document.getElementsByTagName('script')[0]
    invariant(firstScriptTag?.parentNode)
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
  }

  let spotifyController: EmbedController | null = null
  let playbackState: PlaybackState | null = null
  let initialized = false

  const updateSize = (embed: Embed) => {
    if (!initialized) {
      return
    }
    invariant(spotifyController?.iframeElement, 'Container should be defined before updating size')
    const size = sizes[embed.size ?? 'medium'] || sizes.medium
    const width = parsePx(spotifyController.iframeElement, size.width, 'width')
    const height = parsePx(spotifyController.iframeElement, size.height, 'height')

    spotifyController?.setIframeDimensions(width, height)
  }

  const setupPlayer = () => {
    if (!embed.uri) {
      logger.warn('Spotify Embed does not have a URI', embed)
      return
    }

    invariant(window.spotifyIFrameAPI, 'Spotify IFrame API is not loaded')

    const playerEl = document.createElement('div')
    container.appendChild(playerEl)

    const initialSize = sizes[embed.size ?? 'medium'] || sizes.medium

    window.spotifyIFrameAPI.createController(
      playerEl,
      {
        uri: embed.uri,
        width: parsePx(el, initialSize.width, 'width'),
        height: parsePx(el, initialSize.height, 'height'),
      },
      (val: EmbedController) => {
        spotifyController = val

        spotifyController.addListener('ready', () => {
          initialized = true
          updateSize(embed)
        })

        spotifyController.addListener('playback_update', e => {
          playbackState = e.data as PlaybackState
        })

        invariant(spotifyController.iframeElement, 'Spotify IFrame API should return an iframe element')
        spotifyController.iframeElement.setAttribute('referrerpolicy', 'origin')
      }
    )
  }

  if (window.spotifyIFrameAPI) {
    // If window.spotifyIFrameAPI exists, the API is already loaded
    setupPlayer()
  } else {
    // If window.YT exists, the API is already loaded
    window.onSpotifyIframeApiReady = api => {
      window.spotifyIFrameAPI = api
      setupPlayer()
    }
  }

  return {
    setContainerWidth: (width: number) => {
      if (!spotifyController?.iframeElement) {
        return
      }
      invariant(spotifyController?.iframeElement, 'Container should be defined before updating size')
      spotifyController.setIframeDimensions(width, spotifyController.iframeElement.clientHeight)
    },
    hideIFrame: () => Boolean(initialized && spotifyController?.iframeElement),
    getCurrentTime: () => (playbackState?.position ?? 0) / 1000,
    getDuration: () => (playbackState?.duration ?? 0) / 1000,
    isPaused: () => playbackState?.isPaused ?? true,
    setCurrentTime: (time: number) => {
      if (!spotifyController) {
        return
      }
      spotifyController.seek(time)
    },

    updateEmbed: (newEmbed: Embed) => {
      // We don't support updating the URI
      if (newEmbed.uri !== embed.uri) {
        logger.warn('Spotify Embed cannot update the URI', newEmbed, embed)
        return
      }
      // Update the container size
      updateSize(newEmbed)
    },

    cleanup: () => {
      if (spotifyController) {
        spotifyController.destroy()
      }
      container.replaceChildren()
    },
  } satisfies ServiceApi
}

export default {
  parse: parseSpotifyLink,
  sizes,
  getApi,
}
